import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axiosAuth from "../axios";

/**
 * ProductDetailModal — drawer latéral droit avec mode édition intégré
 *
 * Props :
 *   item       — item normalisé { _id, type, name, identifier, status, createdAt, _raw }
 *   scanCount  — nombre de scans
 *   onClose()  — fermer le drawer
 *   onSave(item, editData) — sauvegarder les modifications (géré par UnifiedProductTable)
 *   onDelete() — supprimer / dissocier
 *
 * Notes techniques :
 *   - Deux <div position:fixed> FRÈRES dans le portal (pas imbriqués) pour contourner
 *     le bug iOS Safari : overflow-x:hidden sur <html> casse position:fixed imbriqué.
 *   - Tous les styles sont inline pour éviter les conflits de classes CSS.
 *   - Les boutons héritent de text-transform:uppercase via ui.css — voulu.
 */

const STOCK_LABELS = {
  "en stock":     "En stock",
  "promotion":    "Promotion",
  "indisponible": "Indisponible",
};

const STATUS_OPTIONS = [
  { value: "en stock",     label: "En stock" },
  { value: "promotion",    label: "Promotion" },
  { value: "indisponible", label: "Indisponible" },
];

/* ── Styles ────────────────────────────────────────────────────── */

const s = {
  backdrop: {
    position: "fixed",
    top: 0, left: 0,
    width: "100%", height: "100%",
    zIndex: 8999,
    background: "rgba(15, 23, 42, 0.48)",
  },
  drawer: {
    position: "fixed",
    top: 0, right: 0,
    width: "100%",
    maxWidth: "440px",
    height: "100%",
    zIndex: 9000,
    background: "white",
    display: "flex",
    flexDirection: "column",
    boxShadow: "-8px 0 48px rgba(0,0,0,0.22)",
    overflowY: "auto",
  },
  header: {
    position: "sticky",
    top: 0,
    background: "white",
    zIndex: 1,
    padding: "20px 20px 14px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    padding: "20px",
    overflowY: "auto",
  },
  footer: {
    position: "sticky",
    bottom: 0,
    background: "white",
    borderTop: "1px solid #e2e8f0",
    padding: "14px 20px",
    display: "flex",
    gap: "10px",
    flexShrink: 0,
  },
  sectionBox: (bg) => ({
    marginBottom: "16px",
    padding: "14px",
    background: bg || "#F8FAFC",
    borderRadius: "10px",
  }),
  sectionLabel: {
    fontSize: "0.62rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#94a3b8",
    marginBottom: "10px",
    display: "block",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "100px 1fr",
    gap: "8px 10px",
    alignItems: "baseline",
  },
  label: (mobile) => ({
    fontSize: mobile ? "0.72rem" : "0.78rem",
    fontWeight: 600,
    color: "#64748b",
  }),
  value: (mobile) => ({
    fontSize: mobile ? "0.8rem" : "0.875rem",
    color: "#1e293b",
  }),
  pill: (status) => {
    const base = {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "99px",
      fontSize: "0.65rem",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    };
    if (status === "promotion")    return { ...base, background: "#FEF9C3", color: "#854D0E" };
    if (status === "indisponible") return { ...base, background: "#FFE4E6", color: "#E11D48" };
    return { ...base, background: "#DCFCE7", color: "#166534" };
  },
  typePill: (isBarcode) => ({
    display: "inline-block",
    padding: "2px 9px",
    borderRadius: "99px",
    fontSize: "0.6rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    background: isBarcode ? "#EDE9FE" : "#E0F2FE",
    color: isBarcode ? "#5B21B6" : "#0369A1",
  }),
  closeBtn: {
    background: "#F1F5F9",
    border: "none",
    borderRadius: "8px",
    padding: "6px 11px",
    cursor: "pointer",
    fontSize: "1rem",
    lineHeight: 1,
    color: "#475569",
    flexShrink: 0,
  },
  editInput: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1.5px solid #93c5fd",
    fontSize: "0.875rem",
    background: "#F8FAFC",
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
  },
  editSelect: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1.5px solid #93c5fd",
    fontSize: "0.875rem",
    background: "#F8FAFC",
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
  },
  btnPrimary: {
    flex: 1,
    padding: "11px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.875rem",
    background: "#2563EB",
    color: "white",
    border: "none",
  },
  btnSecondary: {
    flex: 1,
    padding: "11px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.875rem",
    background: "#F1F5F9",
    color: "#475569",
    border: "none",
  },
  btnDanger: {
    flex: 1,
    padding: "11px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.875rem",
    background: "#FFF1F2",
    color: "#E11D48",
    border: "none",
  },
};

/* ── Composant ────────────────────────────────────────────────── */

const ProductDetailModal = ({ item, scanCount, onClose, onSave, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editData, setEditData]   = useState({});

  // Réinitialiser l'état d'édition à chaque item
  useEffect(() => {
    if (!item) return;
    setIsEditing(false);
    setSaving(false);
    setEditData(buildEditData(item));
  }, [item]);

  // ESC : annuler édition si en cours, sinon fermer
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (isEditing) { setIsEditing(false); setEditData(buildEditData(item)); }
      else           { onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, isEditing, onClose]);

  // Scroll lock
  useEffect(() => {
    if (!item) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [item]);

  if (!item) return null;

  const isBarcode = item.type === "barcode";
  const isMobile  = window.innerWidth < 768;
  const raw = item._raw;

  function buildEditData(itm) {
    if (itm.type === "qr") {
      return {
        name:   itm.name || "",
        status: itm.status || "en stock",
      };
    }
    return {
      name:       itm.name || "",
      status:     itm.status || "en stock",
      price:      itm._raw.price != null ? String(itm._raw.price) : "",
      customNote: itm._raw.customNote || "",
    };
  }

  const handleStartEdit = () => {
    setEditData(buildEditData(item));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(buildEditData(item));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item, editData);
      // onSave is responsible for closing (setSelectedItem(null)) on success
      setIsEditing(false);
    } catch (_) {
      // errors toasted by caller
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = () => {
    if (isEditing) { handleCancelEdit(); }
    else           { onClose(); }
  };

  const field = (key, value) => (
    <React.Fragment key={key}>
      <span style={s.label(isMobile)}>{key}</span>
      <span style={s.value(isMobile)}>{value}</span>
    </React.Fragment>
  );

  const drawer = (
    <>
      {/* ── Backdrop ── */}
      <div style={s.backdrop} onClick={handleBackdropClick} />

      {/* ── Drawer panel ── */}
      <div style={{
        ...s.drawer,
        ...(isMobile ? { left: 0, right: 0, maxWidth: "100%", boxShadow: "none" } : {}),
      }}>

        {/* En-tête sticky */}
        <div style={s.header}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 900, color: "#0f172a", lineHeight: 1.3, wordBreak: "break-word" }}>
              {isBarcode ? "📦" : "🔖"} {item.name}
            </h3>
            <span style={s.typePill(isBarcode)}>
              {isBarcode ? "Code-barres" : "QR Code"}
            </span>
            {isEditing && (
              <span style={{ marginLeft: "6px", ...s.typePill(false), background: "#FEF9C3", color: "#854D0E" }}>
                mode édition
              </span>
            )}
          </div>
          <button style={s.closeBtn} onClick={onClose} title="Fermer">✕</button>
        </div>

        {/* Corps scrollable */}
        <div style={s.body}>

          {/* ── Section 1 : Infos produit ── */}
          <div style={s.sectionBox("#F8FAFC")}>
            <span style={s.sectionLabel}>Informations produit</span>
            <div style={s.grid}>

              <span style={s.label(isMobile)}>Identifiant</span>
              <span style={{ fontFamily: "monospace", fontSize: "0.76rem", color: "#475569", wordBreak: "break-all" }}>
                {item.identifier}
              </span>

              <span style={s.label(isMobile)}>Nom</span>
              {isEditing ? (
                <input
                  style={s.editInput}
                  value={editData.name}
                  onChange={(e) => setEditData((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Nom du produit"
                />
              ) : (
                <span style={s.value(isMobile)}>{item.name}</span>
              )}

              <span style={s.label(isMobile)}>Statut</span>
              {isEditing ? (
                <select
                  style={s.editSelect}
                  value={editData.status}
                  onChange={(e) => setEditData((d) => ({ ...d, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <span>
                  <span style={s.pill(item.status)}>
                    {STOCK_LABELS[item.status] || item.status}
                  </span>
                </span>
              )}

              {!isEditing && isBarcode && raw.globalProductId?.category && (
                field("Catégorie", raw.globalProductId.category)
              )}

              {!isEditing && !isBarcode && raw.description && (
                <React.Fragment>
                  <span style={s.label(isMobile)}>Description</span>
                  <span style={{ ...s.value(isMobile), fontStyle: "italic", color: "#475569" }}>{raw.description}</span>
                </React.Fragment>
              )}

            </div>

            {!isEditing && isBarcode && raw.globalProductId?.imageUrl && (
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                <img
                  src={raw.globalProductId.imageUrl}
                  alt={item.name}
                  style={{ maxHeight: "70px", objectFit: "contain", borderRadius: "6px", border: "1px solid #e2e8f0", padding: "4px", background: "white" }}
                />
              </div>
            )}
          </div>

          {/* ── Section 2 : Infos vendeur (barcode) ── */}
          {isBarcode && (
            <div style={s.sectionBox("#F0FDF4")}>
              <span style={s.sectionLabel}>Infos vendeur</span>
              <div style={s.grid}>
                <span style={s.label(isMobile)}>Prix</span>
                {isEditing ? (
                  <input
                    style={s.editInput}
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.price}
                    onChange={(e) => setEditData((d) => ({ ...d, price: e.target.value }))}
                    placeholder="Prix (€)"
                  />
                ) : (
                  <span style={s.value(isMobile)}>
                    {raw.price != null
                      ? <strong>{raw.price} €</strong>
                      : <em style={{ color: "#94a3b8" }}>Non renseigné</em>
                    }
                  </span>
                )}

                <span style={s.label(isMobile)}>Note</span>
                {isEditing ? (
                  <input
                    style={s.editInput}
                    value={editData.customNote}
                    onChange={(e) => setEditData((d) => ({ ...d, customNote: e.target.value }))}
                    placeholder="Note personnalisée"
                  />
                ) : (
                  <span style={{ ...s.value(isMobile), fontStyle: raw.customNote ? "italic" : "normal", color: raw.customNote ? "#475569" : "#94a3b8" }}>
                    {raw.customNote || "—"}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── Section 3 : Statistiques ── */}
          <div style={{ ...s.sectionBox("#FFF7F7"), border: "1px solid #FCE7F3" }}>
            <span style={s.sectionLabel}>Statistiques</span>
            <div style={{ display: "flex", gap: "28px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: "1.75rem", fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>
                  {scanCount != null ? scanCount : "—"}
                </div>
                <div style={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 700, marginTop: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Scans totaux
                </div>
              </div>
              {item.createdAt && (
                <div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1e293b" }}>
                    {new Date(item.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                  <div style={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 700, marginTop: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Date d'ajout
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 4 : Insights achat (barcode uniquement) ── */}
          {isBarcode && <InsightsBlock barcode={item.identifier} />}

        </div>{/* fin body */}

        {/* Pied sticky — actions */}
        <div style={s.footer}>
          {isEditing ? (
            <>
              <button style={s.btnSecondary} onClick={handleCancelEdit} disabled={saving}>
                ✕ Annuler
              </button>
              <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? "⏳ Enregistrement…" : "💾 Enregistrer"}
              </button>
            </>
          ) : (
            <>
              <button style={s.btnSecondary} onClick={handleStartEdit}>
                ✏️ Modifier
              </button>
              <button style={s.btnDanger} onClick={onDelete}>
                {isBarcode ? "🔗 Dissocier" : "🗑️ Supprimer"}
              </button>
            </>
          )}
        </div>

      </div>{/* fin drawer */}
    </>
  );

  return createPortal(drawer, document.body);
};

// ── Insights achat ────────────────────────────────────────────────────────
function InsightsBlock({ barcode }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosAuth.get(`/scans/stats/barcode/${barcode}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [barcode]);

  const sectionStyle = {
    marginBottom: "16px", padding: "14px",
    background: "#F0F9FF", borderRadius: "10px",
    border: "1px solid #BAE6FD",
  };
  const labelStyle = {
    fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase",
    letterSpacing: "0.08em", color: "#94a3b8", marginBottom: "10px", display: "block",
  };

  if (loading) return (
    <div style={sectionStyle}>
      <span style={labelStyle}>Insights</span>
      <p style={{ fontSize: "12px", color: "#94a3b8" }}>Chargement…</p>
    </div>
  );

  if (!data) return null;

  const { total_crushes, active_alerts, purchased, conversion_rate, avg_delay_hours, demographics, crushes_per_week } = data;
  const noData = purchased === 0 && conversion_rate === 0;

  const maxWeek = Math.max(...crushes_per_week.map(w => w.count), 1);
  const genderColors = { homme: "#3B82F6", femme: "#EC4899", autre: "#8B5CF6", non_renseigne: "#CBD5E1" };
  const genderLabels = { homme: "Hommes", femme: "Femmes", autre: "Autre", non_renseigne: "N/R" };
  const totalGender = Object.values(demographics.gender).reduce((a, b) => a + b, 0);

  return (
    <div style={sectionStyle}>
      <span style={labelStyle}>Insights</span>

      {/* 3 métriques en ligne */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "14px" }}>
        {[
          { value: total_crushes, label: "Crushes" },
          { value: active_alerts, label: "Alertes actives" },
          { value: purchased,     label: "Achats" },
        ].map(({ value, label }) => (
          <div key={label} style={{ flex: 1, background: "white", borderRadius: "8px", padding: "10px 8px", textAlign: "center", border: "1px solid #E0F2FE" }}>
            <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 700, marginTop: "4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
          </div>
        ))}
      </div>

      {noData ? (
        <p style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center", margin: "8px 0" }}>
          Pas encore d'achat enregistré via CrushNow
        </p>
      ) : (
        <>
          {/* Taux de conversion */}
          <div style={{ marginBottom: "10px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0369a1" }}>
              Taux d'achat : {conversion_rate}%
            </span>
            {avg_delay_hours != null && (
              <span style={{ fontSize: "12px", color: "#475569", marginLeft: "10px" }}>
                · Délai moyen crush→achat : {avg_delay_hours}h
              </span>
            )}
          </div>
        </>
      )}

      {/* Démographie genre */}
      {totalGender > 0 && (
        <div style={{ marginBottom: "14px" }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Genre</div>
          <div style={{ display: "flex", gap: "6px", alignItems: "flex-end", height: "48px" }}>
            {Object.entries(demographics.gender).map(([key, count]) => {
              const pct = totalGender > 0 ? Math.round((count / totalGender) * 100) : 0;
              return (
                <div key={key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                  <div style={{ width: "100%", height: `${Math.max(pct * 0.4, count > 0 ? 4 : 0)}px`, background: genderColors[key], borderRadius: "3px 3px 0 0", transition: "height 0.3s" }} />
                  <span style={{ fontSize: "9px", color: "#64748b", fontWeight: 600 }}>{genderLabels[key]}</span>
                  {count > 0 && <span style={{ fontSize: "9px", color: "#94a3b8" }}>{pct}%</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Crushes par semaine */}
      <div>
        <div style={{ fontSize: "0.62rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Crushs / semaine</div>
        <div style={{ display: "flex", gap: "4px", alignItems: "flex-end", height: "52px" }}>
          {crushes_per_week.map(({ week, count }) => (
            <div key={week} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
              <div style={{ width: "100%", height: `${Math.max((count / maxWeek) * 44, count > 0 ? 3 : 1)}px`, background: count > 0 ? "#D41A6E" : "#F1F5F9", borderRadius: "3px 3px 0 0", transition: "height 0.3s" }} />
              <span style={{ fontSize: "8px", color: "#94a3b8", writingMode: "vertical-rl", transform: "rotate(180deg)", lineHeight: 1 }}>
                {week.replace(/^\d{4}-/, "")}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
